
export interface TimelineDay {
  day: number;
  dayLabel: string;
  title: string;
  description: string;
  tasks: {
    text: string;
    link?: string;
    linkLabel?: string;
  }[];
  icon: string;
  color: string;
  proTip?: string;
}

export const first30DaysTimeline: TimelineDay[] = [
  {
    day: 1,
    dayLabel: 'Day 1',
    title: 'Arrival & Essentials',
    description: 'Get settled into your new home and handle the most urgent basics so you can sleep easy on night one.',
    tasks: [
      { text: 'Confirm utilities are active (electric, water, gas)' },
      { text: 'Test internet connection or schedule installation' },
      { text: 'Locate nearest grocery store — Safeway on Douglas Blvd or Raley\'s on Fairway Dr' },
      { text: 'Find the closest urgent care: Sutter Walk-In Care on Eureka Rd' },
      { text: 'Download the Roseville 311 app for city services' },
    ],
    icon: 'ri-home-heart-line',
    color: '#F59E0B',
    proTip: 'Keep a \"first night\" box with essentials: phone chargers, toiletries, snacks, sheets, and a change of clothes.',
  },
  {
    day: 3,
    dayLabel: 'Days 2–3',
    title: 'Neighborhood Orientation',
    description: 'Explore your immediate surroundings and start building a mental map of your new community.',
    tasks: [
      { text: 'Walk or drive your neighborhood — find the nearest park, mailbox, and gas station' },
      { text: 'Introduce yourself to at least 2 neighbors' },
      { text: 'Locate your assigned trash, recycling, and green waste pickup day' },
      { text: 'Find your nearest post office and update your mailing address at USPS.com' },
      { text: 'Scout out a go-to coffee shop — try Bloom Coffee on Vernon St' },
    ],
    icon: 'ri-compass-discover-line',
    color: '#10B981',
    proTip: 'Join the Nextdoor app immediately — it\'s the fastest way to learn about your specific neighborhood.',
  },
  {
    day: 5,
    dayLabel: 'Days 4–5',
    title: 'Government & Documentation',
    description: 'Tackle the official paperwork early so deadlines don\'t sneak up on you.',
    tasks: [
      { text: 'Schedule a DMV appointment for your CA driver\'s license (due within 10 days)', link: '/relocation#dmv', linkLabel: 'See DMV Checklist' },
      { text: 'Get a smog check for your vehicle at a local STAR station' },
      { text: 'Update your voter registration at registertovote.ca.gov' },
      { text: 'Visit Roseville Public Library on Civic Center Dr for a free library card' },
      { text: 'Update your address with your bank, insurance, and subscriptions' },
    ],
    icon: 'ri-file-list-3-line',
    color: '#3B82F6',
    proTip: 'The Roseville DMV on Washington Blvd is less crowded than Sacramento locations. Book online for the shortest wait.',
  },
  {
    day: 7,
    dayLabel: 'Days 6–7',
    title: 'Healthcare & Safety',
    description: 'Set up your healthcare network and learn the safety essentials for your new area.',
    tasks: [
      { text: 'Choose a primary care physician — Sutter Health and Kaiser both have Roseville campuses' },
      { text: 'Find a dentist and schedule a checkup' },
      { text: 'Transfer prescriptions to a local pharmacy' },
      { text: 'Locate the nearest fire station and learn your evacuation zone at community.zonehaven.com' },
      { text: 'Save Roseville PD non-emergency number: (916) 774-5000' },
    ],
    icon: 'ri-heart-pulse-line',
    color: '#EF4444',
    proTip: 'Sutter Roseville Medical Center is a Level II Trauma Center — one of the best in the Sacramento region.',
  },
  {
    day: 10,
    dayLabel: 'Days 8–10',
    title: 'Schools & Childcare',
    description: 'If you have kids, get enrollment sorted early. Popular programs fill up fast in Roseville.',
    tasks: [
      { text: 'Enroll children in school — visit your district office with proof of residency', link: '/relocation#schools', linkLabel: 'View School Districts' },
      { text: 'Tour potential schools and meet with counselors' },
      { text: 'Research after-school programs through Roseville Parks & Rec' },
      { text: 'Look into childcare options — KinderCare and Bright Horizons have local centers' },
      { text: 'Sign up for school district email alerts and parent portals' },
    ],
    icon: 'ri-graduation-cap-line',
    color: '#8B5CF6',
    proTip: 'Registration opens in spring for the following year. If you arrive mid-year, call the school directly for expedited enrollment.',
  },
  {
    day: 14,
    dayLabel: 'Days 11–14',
    title: 'Explore Roseville',
    description: 'Time to discover what makes Roseville special. Get out and experience the city like a local.',
    tasks: [
      { text: 'Walk or bike the Miners Ravine Trail — 10+ miles of scenic creek-side paths' },
      { text: 'Visit the Westfield Galleria for shopping and dining' },
      { text: 'Explore Historic Old Town Roseville — boutiques, breweries, and the Tower Theatre' },
      { text: 'Check out Maidu Regional Park for hiking and the Maidu Museum' },
      { text: 'Hit the Saturday Farmers Market at the Fountains' },
    ],
    icon: 'ri-map-pin-line',
    color: '#EC4899',
    proTip: 'The Roseville Visitors Guide (free at City Hall) has discount coupons for local restaurants and attractions.',
  },
  {
    day: 21,
    dayLabel: 'Days 15–21',
    title: 'Build Your Community',
    description: 'Start putting down roots. Roseville has an incredibly welcoming community — lean into it.',
    tasks: [
      { text: 'Join a local gym or fitness studio — The Grounds, Orangetheory, or the Roseville Sports Center' },
      { text: 'Attend a community event — check roseville.ca.us/events for the calendar' },
      { text: 'Find a place of worship if that\'s important to you — Roseville has 100+ congregations' },
      { text: 'Join a local club or group — running clubs, book clubs, or volunteer organizations' },
      { text: 'Try 3 new local restaurants this week', link: '/food-drink', linkLabel: 'Browse Restaurants' },
    ],
    icon: 'ri-group-line',
    color: '#14B8A6',
    proTip: 'Roseville Parks & Rec offers adult sports leagues, art classes, and social groups — perfect for meeting people.',
  },
  {
    day: 30,
    dayLabel: 'Days 22–30',
    title: 'Settle In & Thrive',
    description: 'By now you\'re becoming a true Roseville local. Wrap up loose ends and start enjoying your new life.',
    tasks: [
      { text: 'Complete vehicle registration at the DMV (due within 20 days)' },
      { text: 'Set up autopay for all utilities and subscriptions' },
      { text: 'Create a household emergency kit (earthquakes are rare but possible)' },
      { text: 'Plan a day trip — Lake Tahoe (90 min), San Francisco (2 hrs), or Napa Valley (1.5 hrs)' },
      { text: 'Invite your new neighbors over for a housewarming gathering' },
    ],
    icon: 'ri-star-smile-line',
    color: '#F59E0B',
    proTip: 'You\'re officially a Roseville local now! Consider subscribing to the Roseville Press Tribune to stay connected with community news.',
  },
];
